/**
 * @file: useReportData.ts
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 */

import { VehicleReportProjection } from '@euler/model/report';
import { useServiceFactory } from '@euler/services/factory';
import { Maybe } from '@euler/types';
import { useRefreshableAsyncResource } from '@euler/utils/hooks';
import qs from 'qs';
import { useCallback } from 'react';

export type ReportRef = (
  | { type: 'task'; id: string }
  | { type: 'report'; id: string }
) & {
  orderNo: string;
  reportNo: string;
  licensePlateNo: string;
  vehicleName: string;
  vehicleImageUrl: Maybe<string>;
};

export const getReportResourceId = (
  target: ReportRef,
  projection: VehicleReportProjection,
) => {
  return `report:${target.type}:${target.id}:${qs.stringify(projection)}`;
};

export function useReportData(
  target: ReportRef,
  projection: VehicleReportProjection,
) {
  const { taskService, reportService } = useServiceFactory();
  const { type, id } = target;
  const fetch = useCallback(async () => {
    if (type == 'task') {
      return await taskService.getReportWithPendingData(id, projection);
    } else {
      return await reportService.getReportDetail(id, projection);
    }
  }, [type, taskService, id, projection, reportService]);

  const key = getReportResourceId(target, projection);
  const {
    value: report,
    isRefreshing,
    refresh,
    error,
  } = useRefreshableAsyncResource(fetch, key);

  return {
    report,
    isRefreshing,
    refresh,
    error,
  };
}
